import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { getServerBase } from './server';
import type { Coords, Ride, ServerConfig } from './types';

/** Fehler mit HTTP-Status, damit die Oberfläche 404 von 500 unterscheiden kann. */
export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Exportiert, damit connections.ts denselben Fehler-/Parsing-Weg nutzen kann. */
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    // In der Android-App die eingerichtete Serveradresse davor, im Browser
    // bleibt der Pfad relativ (getServerBase() ist dort leer).
    response = await fetch(path.startsWith('/') ? `${getServerBase()}${path}` : path, {
      ...init,
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    throw new ApiError('Keine Verbindung zum Server.', 0);
  }

  const text = await response.text();

  let payload: any = {};
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // Antwort ist kein JSON. Genau das passiert in der Android-App, wenn
      // keine Serveradresse gesetzt ist: Capacitors lokaler Server liefert
      // für jeden unbekannten Pfad die index.html aus - mit Status 200.
      // Früher wurde daraus stillschweigend ein leeres Objekt, die App hielt
      // das für einen Erfolg und stürzte erst später über die fehlenden
      // Felder ab. Lieber hier ehrlich scheitern.
      throw new ApiError(
        response.ok
          ? 'Der Server hat keine gültige Antwort geschickt. Stimmt die Serveradresse?'
          : 'Da ist etwas schiefgelaufen.',
        response.status,
      );
    }
  }

  if (!response.ok) {
    const message = typeof payload?.error === 'string' ? payload.error : 'Da ist etwas schiefgelaufen.';
    throw new ApiError(message, response.status);
  }
  return payload as T;
}

/**
 * Wie `request`, aber in der Android-App über die **native** HTTP-Schicht.
 *
 * Der Grund steht im README des Background-Geolocation-Plugins: Android
 * drosselt HTTP-Anfragen aus der WebView nach etwa fünf Minuten im
 * Hintergrund. Genau dann fährt man aber - und genau dann müssen die
 * Positionen ankommen, sonst bleibt der Zustandsautomat stehen und der
 * Mitfahrer bekommt keine Benachrichtigung.
 *
 * Bewusst nur für diesen einen heißen Pfad statt `CapacitorHttp` global
 * einzuschalten: das würde `window.fetch` in der ganzen App ersetzen und
 * damit auch jeden Aufruf betreffen, der heute unauffällig funktioniert.
 */
async function postThroughNative<T>(path: string, body: unknown): Promise<T> {
  const url = `${getServerBase()}${path}`;
  let response: { status: number; data: unknown };
  try {
    response = await CapacitorHttp.post({
      url,
      headers: { 'content-type': 'application/json' },
      data: body,
    });
  } catch {
    throw new ApiError('Keine Verbindung zum Server.', 0);
  }

  // Die native Schicht parst JSON meist schon selbst - bei anderem
  // Content-Type kommt eine Zeichenkette zurück.
  let payload: any = response.data;
  if (typeof payload === 'string') {
    if (payload === '') {
      payload = {};
    } else {
      try {
        payload = JSON.parse(payload);
      } catch {
        throw new ApiError('Der Server hat keine gültige Antwort geschickt.', response.status);
      }
    }
  }

  if (response.status < 200 || response.status >= 300) {
    const message = typeof payload?.error === 'string' ? payload.error : 'Da ist etwas schiefgelaufen.';
    throw new ApiError(message, response.status);
  }
  return payload as T;
}

/** Im Browser der normale Weg, in der App der native. */
function postResilient<T>(path: string, body: unknown): Promise<T> {
  return Capacitor.isNativePlatform()
    ? postThroughNative<T>(path, body)
    : request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export const api = {
  config: () => request<ServerConfig>('/api/config'),

  createRide: (body: {
    driverName: string;
    passengerName?: string;
    origin: Coords;
    pickup?: Coords | null;
    estimatedTime?: number | null;
  }) =>
    request<{ ride: Ride; token: string; code: string }>('/api/rides', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  join: (code: string) =>
    request<{ ride: Ride; token: string }>(`/api/join/${encodeURIComponent(code.toUpperCase())}`),

  get: (id: string, token: string) =>
    request<{ ride: Ride }>(`/api/rides/${id}?token=${encodeURIComponent(token)}`),

  setPickup: (id: string, token: string, pickup: Coords) =>
    request<{ ride: Ride }>(`/api/rides/${id}/pickup`, {
      method: 'POST',
      body: JSON.stringify({ token, pickup }),
    }),

  setOrigin: (id: string, token: string, origin: Coords) =>
    request<{ ride: Ride }>(`/api/rides/${id}/origin`, {
      method: 'POST',
      body: JSON.stringify({ token, origin }),
    }),

  sendLocation: (
    id: string,
    token: string,
    fix: { lat: number; lng: number; accuracy?: number | null; at?: number },
  ) =>
    postResilient<{ ride: Ride; events: Array<{ type: string; title: string; body: string }> }>(
      `/api/rides/${id}/location`,
      { token, ...fix },
    ),

  heartbeat: (id: string, token: string) =>
    postResilient<{ ride: Ride }>(`/api/rides/${id}/heartbeat`, { token }),

  subscribe: (id: string, token: string, subscription: PushSubscriptionJSON) =>
    request<{ ok: true; subscriptions: number }>(`/api/rides/${id}/subscribe`, {
      method: 'POST',
      body: JSON.stringify({ token, subscription }),
    }),

  testPush: (id: string, token: string) =>
    request<{ sent: number; removed: number }>(`/api/rides/${id}/test-push`, {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  cancel: (id: string, token: string) =>
    request<{ ride: Ride }>(`/api/rides/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
};
